import { FC } from 'react';

import { PunchupLineItemList } from 'features/common/components/Punchup/primitives/display/PunchupLineItemList';

import { PunchupSendTicketsConfirmation } from '../PunchupSendTicketsConfirmation';
import styles from './PunchupBulkConfirmation.module.css';

export interface BulkRecipientRow {
	email: string;
	quantity: number;
	ticketTypeName: string | null;
	unitPrice: number | null;
}

export interface PunchupBulkConfirmationProps {
	rows: BulkRecipientRow[];
	isSending: boolean;
	onSend: () => void;
	onCancel: () => void;
}

interface RecipientGroup {
	email: string;
	lines: BulkRecipientRow[];
}

/** Collapse multiple CSV rows for the same email into a single recipient block. */
const groupByEmail = (rows: BulkRecipientRow[]): RecipientGroup[] => {
	const map = new Map<string, BulkRecipientRow[]>();
	for (const row of rows) {
		const key = row.email.toLowerCase().trim();
		const group = map.get(key) ?? [];
		group.push(row);
		map.set(key, group);
	}
	return Array.from(map.entries()).map(([, lines]) => ({ email: lines[0].email, lines }));
};

export const PunchupBulkConfirmation: FC<PunchupBulkConfirmationProps> = ({ rows, isSending, onSend, onCancel }) => {
	const totalPrice = rows.reduce((s, r) => s + r.quantity * (r.unitPrice ?? 0), 0);
	const groups = groupByEmail(rows);

	return (
		<PunchupSendTicketsConfirmation isSending={isSending} onSend={onSend} onCancel={onCancel} totalPrice={totalPrice}>
			{groups.map((group) => (
				<div key={group.email} className={styles.recipientBlock}>
					<p className={styles.email}>To: {group.email}</p>
					<PunchupLineItemList
						items={group.lines
							.filter((row) => row.quantity > 0 && row.ticketTypeName)
							.map((row) => ({
								label: row.ticketTypeName as string,
								count: row.quantity,
								price: row.quantity * (row.unitPrice ?? 0),
							}))}
					/>
				</div>
			))}
		</PunchupSendTicketsConfirmation>
	);
};
